import React, { useState, useEffect, Suspense, lazy } from 'react';
import MobileDock from './MobileDock';
import MobileMusicPlayer from './MobileMusicPlayer';
import VideoPlayer from './VideoPlayer';
import Members from './Members';
import Settings from './Settings';
import WelcomeScreen from './WelcomeScreen';
import { urlFor, fetchWithCache } from '../sanityClient';

const NewsFeed = lazy(() => import('./NewsFeed'));
const PhotoGallery = lazy(() => import('./PhotoGallery'));
const FeedbackWindow = lazy(() => import('./FeedbackWindow'));
const ArchiveLightTable = lazy(() => import('./Lightbox/ArchiveLightTable'));

const MobileEnvironment = () => {
    // Tab State
    const [activeTab, setActiveTab] = useState('video');
    const [user, setUser] = useState(null);
    const [currentTheme, setCurrentTheme] = useState('paradise');
    const [showWelcome, setShowWelcome] = useState(false);
    const [showFeedback, setShowFeedback] = useState(false);
    const [hasNewNews, setHasNewNews] = useState(false);
    const [latestNewsDate, setLatestNewsDate] = useState(null);

    const handleNewsContentLoaded = (latestDate) => {
        setLatestNewsDate(latestDate);
        const lastSeen = localStorage.getItem('paradise_last_seen_news');
        if (!lastSeen || new Date(latestDate) > new Date(lastSeen)) {
            setHasNewNews(true);
        }
    };

    // When Newsroom tab opens, mark news as seen
    useEffect(() => {
        if (activeTab === 'images' && latestNewsDate) {
            setHasNewNews(false);
            localStorage.setItem('paradise_last_seen_news', latestNewsDate);
        }
    }, [activeTab, latestNewsDate]);

    useEffect(() => {
        const hasVisited = localStorage.getItem('paradise_welcome_seen');
        if (!hasVisited) {
            setShowWelcome(true);
        }

        const storedUser = localStorage.getItem('paradise_user');
        if (storedUser) {
            try {
                setUser(JSON.parse(storedUser));
            } catch (e) {
                console.error("Failed to parse stored user", e);
            }
        }
    }, []);

    const themeColors = {
        paradise: '#e6e2d3',
        lagoon: '#b8e2f2',
        sunset: '#f5d0b5',
        jungle: '#dbe7c9'
    };

    // Music State (Lifted from MusicPlayer)
    const [isPlaying, setIsPlaying] = useState(false);
    const [volume, setVolume] = useState(0.8);
    const [isMuted] = useState(false);
    const [playlists, setPlaylists] = useState({});
    const [currentPlaylistId, setCurrentPlaylistId] = useState('poolsuite');
    const [currentTrackIndex, setCurrentTrackIndex] = useState(0);

    // Fetch Music Data
    useEffect(() => {
        const fetchData = async () => {
            try {
                const trackQuery = `*[_type == "track"] | order(title asc) {
                    title,
                    artist,
                    "src": soundCloudUrl,
                    coverImage,
                    playlist->{ _id, title }
                }`;
                const trackData = await fetchWithCache(trackQuery);

                if (trackData) {
                    const grouped = trackData.reduce((acc, track) => {
                        const plId = track.playlist?._id || 'uncategorized';
                        const plTitle = track.playlist?.title || 'Uncategorized';
                        if (!acc[plId]) acc[plId] = { title: plTitle, tracks: [] };
                        acc[plId].tracks.push({
                            ...track,
                            cover: track.coverImage ? urlFor(track.coverImage).width(200).auto('format').url() : null
                        });
                        return acc;
                    }, {});
                    setPlaylists(grouped);
                }
            } catch (error) {
                console.error("Error fetching media:", error);
            }
        };
        fetchData();
    }, []);

    // Set default playlist
    useEffect(() => {
        const keys = Object.keys(playlists);
        if (keys.length > 0 && !playlists[currentPlaylistId]) {
            setCurrentPlaylistId(keys[0]);
        }
    }, [playlists, currentPlaylistId]);

    // Audio Logic
    const currentPlaylistObj = playlists[currentPlaylistId];
    const currentPlaylist = currentPlaylistObj?.tracks || [];
    const currentTrack = currentPlaylist[currentTrackIndex];

    // Handlers
    const togglePlay = () => setIsPlaying(!isPlaying);
    const nextTrack = () => setCurrentTrackIndex((prev) => (prev + 1) % currentPlaylist.length);
    const switchPlaylist = (id) => {
        if (id === currentPlaylistId) return;
        setCurrentPlaylistId(id);
        setCurrentTrackIndex(0);
        setIsPlaying(true);
    };

    // Handle Deep Links (Members) and Stripe Success
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('token')) {
            setActiveTab('members');
        }

        // Always refresh user status to ensure up-to-date permissions
        const storedUser = localStorage.getItem('paradise_user');
        if (storedUser) {
            try {
                const parsedUser = JSON.parse(storedUser);
                if (parsedUser.id) {
                    const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:4000';
                    const storedToken = localStorage.getItem('paradise_token');
                    if (!storedToken) return;
                    fetch(`${API_BASE}/api/auth/status`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${storedToken}`
                        },
                        body: JSON.stringify({ userId: parsedUser.id })
                    })
                        .then(res => res.json())
                        .then(data => {
                            if (data.user) {
                                setUser(data.user);
                                localStorage.setItem('paradise_user', JSON.stringify(data.user));
                                // Only switch tab if deep linked or just signed up
                                if (params.get('token')) setActiveTab('members');
                                if (params.get('success') === 'true') {
                                    setActiveTab('members');
                                    // Clean URL
                                    const newUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
                                    window.history.replaceState({ path: newUrl }, '', newUrl);
                                }
                            }
                        })
                        .catch(err => console.error("Failed to refresh user status", err));
                }
            } catch (e) {
                console.error("Failed to refresh session", e);
            }
        } else {
            // Handle parameters even if no user stored initially (edge case)
            if (params.get('token')) setActiveTab('members');
        }
    }, []);

    return (
        <div
            className="fixed inset-0 flex flex-col font-sans select-none overflow-hidden transition-colors duration-500"
            style={{ backgroundColor: themeColors[currentTheme] }}
        >
            {/* Feedback Window Overlay */}
            {showFeedback && user && (
                <div className="absolute inset-0 z-[60]">
                    <Suspense fallback={<div className="p-8 text-center font-mono text-xs">LOADING...</div>}>
                        <FeedbackWindow user={user} onClose={() => setShowFeedback(false)} />
                    </Suspense>
                </div>
            )}
            {/* Background Pattern */}
            <div className="absolute inset-0 pointer-events-none opacity-10 bg-[linear-gradient(rgba(0,0,0,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.1)_1px,transparent_1px)] bg-[length:20px_20px]"></div>

            {/* Header */}
            <div className="h-12 flex-shrink-0 flex items-center justify-between px-4 bg-[#f4f4f4] border-b border-white/40 relative z-50 pt-safe shadow-[0_2px_4px_rgba(0,0,0,0.05)]">
                <button
                    onClick={() => setShowWelcome(true)}
                    className="flex items-center gap-2 px-3 py-1.5 bg-[#e8e6df] border border-[#d1d1d1] rounded hover:bg-[#dcdad3] active:scale-95 transition-all shadow-sm"
                >
                    <div className="w-2 h-2 rounded-full bg-[#ff4400]"></div>
                    <span className="text-[10px] font-bold tracking-widest uppercase text-[#444444]">SYSTEM INFO</span>
                </button>

                {!user ? (
                    <button
                        onClick={() => setActiveTab('members')}
                        className="px-3 py-1.5 bg-[#e8e6df] border border-[#d1d1d1] rounded hover:bg-[#dcdad3] active:scale-95 transition-all shadow-sm"
                    >
                        <span className="text-[10px] font-bold tracking-widest uppercase text-[#444444]">BECOME A MEMBER</span>
                    </button>
                ) : (
                    <button
                        onClick={() => setActiveTab('members')}
                        className="px-3 py-1.5 bg-[#e8e6df] border border-[#d1d1d1] rounded hover:bg-[#dcdad3] active:scale-95 transition-all shadow-sm flex items-center gap-2"
                    >
                        <div className="w-1.5 h-1.5 rounded-full bg-[#ff4400]"></div>
                        <span className="font-mono text-[9px] font-bold tracking-wider truncate max-w-[120px] text-[#444444]">
                            {user.email || 'MEMBER'}
                        </span>
                    </button>
                )}
            </div>

            {/* Welcome Screen Overlay */}
            {showWelcome && (
                <div className="absolute inset-0 z-[100]">
                    <WelcomeScreen
                        onClose={() => {
                            setShowWelcome(false);
                            localStorage.setItem('paradise_welcome_seen', 'true');
                        }}
                        onMinimize={() => {
                            setShowWelcome(false);
                            localStorage.setItem('paradise_welcome_seen', 'true');
                        }}
                        isMobile={true}
                    />
                </div>
            )}

            {/* Main Content Area */}
            <div className="flex-1 relative overflow-hidden bg-[#dcdad3]">
                {/* Video Tab */}
                <div className={`absolute inset-0 ${activeTab === 'video' ? 'z-10' : 'z-0 invisible'}`}>
                    <VideoPlayer isActive={activeTab === 'video'} user={user} isMobile={true} onOpenMembers={() => setActiveTab('members')} />
                </div>

                {/* Photos Tab */}
                {activeTab === 'photos' && (
                    <div className="absolute inset-0 z-10 bg-[#e8e8e8]">
                        <Suspense fallback={<div className="p-8 text-center font-mono text-xs">LOADING...</div>}>
                            <PhotoGallery user={user} onOpenMembers={() => setActiveTab('members')} />
                        </Suspense>
                    </div>
                )}

                {/* Newsroom Tab */}
                {activeTab === 'images' && (
                    <div className="absolute inset-0 z-10 bg-[#e8e6df]">
                        <Suspense fallback={<div className="p-8 text-center font-mono text-xs">LOADING...</div>}>
                            <NewsFeed onContentLoaded={handleNewsContentLoaded} />
                        </Suspense>
                    </div>
                )}

                {/* Lightbox Tab */}
                {activeTab === 'lightbox' && (
                    <div className="absolute inset-0 z-10 bg-[#1a1a1a]">
                        <Suspense fallback={<div className="p-8 text-center font-mono text-xs text-white">LOADING...</div>}>
                            <ArchiveLightTable user={user} />
                        </Suspense>
                    </div>
                )}

                {/* Members Tab */}
                {activeTab === 'members' && (
                    <div className="absolute inset-0 z-20 bg-[#dcdad3]">
                        <Members user={user} setUser={setUser} />
                    </div>
                )}

                {/* Settings Tab */}
                {activeTab === 'settings' && (
                    <div className="absolute inset-0 z-20 bg-[#dcdad3]">
                        <Settings
                            currentTheme={currentTheme}
                            onThemeChange={setCurrentTheme}
                            onOpenFeedback={(user && (user.membershipTier === 'club' || user.membershipTier === 'executive' || user.isPhotographer)) ? () => setShowFeedback(true) : null}
                        />
                    </div>
                )}


            </div>


            {/* Compact Music Player (Always Visible) */}
            <MobileMusicPlayer
                currentTrack={currentTrack}
                isPlaying={isPlaying}
                togglePlay={togglePlay}
                nextTrack={nextTrack}
                volume={volume}
                setVolume={setVolume}
                isMuted={isMuted}
                onEnded={() => nextTrack()}
                playlists={playlists}
                currentPlaylistId={currentPlaylistId}
                switchPlaylist={switchPlaylist}
                isCompact={activeTab === 'photos' || activeTab === 'images' || activeTab === 'settings' || activeTab === 'members' || activeTab === 'lightbox'}
            />

            {/* Dock */}
            <MobileDock
                activeTab={activeTab}
                onTabChange={setActiveTab}
                hasNewNews={hasNewNews}
            />
        </div>
    );
};

export default MobileEnvironment;
